export interface RuntimeConfig {
  appName: string;
  publicBaseUrl: string;
  oss: {
    bucket: string;
    region: string;
    publicBaseUrl: string;
    uploadDir: string;
    expiresSeconds: number;
  };
  workbench: {
    authMode: "persistent" | "memory" | "disabled";
    targetSpaceId: number;
  };
}

export interface DatabaseInfo {
  key: string;
  name: string;
  host: string;
  port: number;
  database: string;
  readonly: boolean;
}

export interface DatabasePingResult extends DatabaseInfo {
  ok: boolean;
  message: string;
}

export interface OssSignature {
  uploadUrl: string;
  publicUrl: string;
  method: "POST";
  key: string;
  expiresAt: string;
  fields: Record<string, string>;
}

export interface WorkbenchApiEnvelope<T = unknown> {
  code: string;
  message: string;
  data: T | null;
}

export interface WorkbenchApiResult<T = unknown> {
  ok: boolean;
  status: number;
  body: WorkbenchApiEnvelope<T>;
}

export interface WorkbenchLoginData {
  access_token: string;
  token_expires_at: string;
  must_change_password: boolean;
  user: {
    id: string;
    phone_masked: string;
    is_admin: boolean;
  };
  entitlement: {
    eligible: boolean;
    status: string;
    expires_at: string;
    source: string;
  };
}

export interface WorkbenchCurrentUserData {
  token_expires_at: string;
  must_change_password: boolean;
  user: WorkbenchLoginData["user"];
  entitlement: WorkbenchLoginData["entitlement"];
}

export async function fetchConfig(): Promise<RuntimeConfig> {
  return requestJson<RuntimeConfig>("/api/config");
}

export async function fetchDatabases(): Promise<DatabaseInfo[]> {
  const result = await requestJson<{ databases: DatabaseInfo[] }>("/api/dbs");
  return result.databases;
}

export async function pingDatabases(): Promise<DatabasePingResult[]> {
  const result = await requestJson<{ databases: DatabasePingResult[] }>("/api/dbs/ping");
  return result.databases;
}

export async function createUploadSignature(file: File): Promise<OssSignature> {
  return requestJson<OssSignature>("/api/uploads/signature", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      filename: file.name,
      contentType: file.type
    })
  });
}

export async function uploadToOss(file: File, signature: OssSignature): Promise<string> {
  const formData = new FormData();
  Object.entries(signature.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  if (file.type) {
    formData.append("Content-Type", file.type);
  }
  formData.append("file", file);

  const response = await fetch(signature.uploadUrl, {
    method: signature.method,
    body: formData
  });

  if (!response.ok) {
    throw new Error(`OSS upload failed: ${response.status}`);
  }

  return signature.publicUrl;
}

export async function callWorkbenchApi<T = unknown>(
  path: string,
  init: RequestInit = {},
  accessToken?: string
): Promise<WorkbenchApiResult<T>> {
  const headers = new Headers(init.headers);
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }
  const response = await fetch(`/api/workbench/v1/auth${path}`, {
    ...init,
    headers
  });
  const payload: unknown = await response.json();
  return {
    ok: response.ok,
    status: response.status,
    body: normalizeWorkbenchEnvelope<T>(payload)
  };
}

async function requestJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  const payload: unknown = await response.json();
  if (!response.ok) {
    const message = readMessage(payload) ?? `Request failed: ${response.status}`;
    throw new Error(message);
  }
  return payload as T;
}

function readMessage(value: unknown): string | undefined {
  if (typeof value !== "object" || value === null) {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  return typeof record.message === "string" ? record.message : undefined;
}

function normalizeWorkbenchEnvelope<T>(value: unknown): WorkbenchApiEnvelope<T> {
  if (typeof value !== "object" || value === null) {
    return { code: "INVALID_RESPONSE", message: "接口返回格式不正确", data: null };
  }
  const record = value as Record<string, unknown>;
  return {
    code: typeof record.code === "string" ? record.code : "UNKNOWN",
    message: typeof record.message === "string" ? record.message : "",
    data: (record.data ?? null) as T | null
  };
}
